/**
 * PTV2-1327 — shared save-activity state, aggregated into the global "Saved / Saving… / Unsaved"
 * status shown in the patient-record header and the pedigree toolbar.
 *
 * Each {@link useEditableDraft} instance publishes two things here, keyed by a stable instance id and
 * tagged with its dirty scope (`null` = patient record, person guid = a pedigree node):
 *   • **pending** — a debounced autosave is armed (carries when it will fire, for a countdown);
 *   • **saving** — a commit is in flight (the card is optimistically clean, so the dirty registry no
 *     longer reports it — this is how the status still shows "Saving…").
 *
 * Zustand so the status components re-render as activity changes.
 */
import { create } from 'zustand';

/** A single instance's pending (armed) autosave. */
interface PendingAutosave {
  /** The dirty scope — `null` on the patient record, the person guid in the pedigree node editor. */
  readonly scopeId: string | null;
  /** Epoch-ms at which the debounced autosave will commit (recomputed on every keystroke). */
  readonly savesAt: number;
}

interface AutosaveStatusState {
  readonly pending: Readonly<Record<string, PendingAutosave>>;
  /** instance id → scope, for commits currently in flight. */
  readonly saving: Readonly<Record<string, string | null>>;
  readonly setPending: (id: string, entry: PendingAutosave) => void;
  readonly clearPending: (id: string) => void;
  readonly setSaving: (id: string, scopeId: string | null) => void;
  readonly clearSaving: (id: string) => void;
}

function without<V>(map: Readonly<Record<string, V>>, id: string): Readonly<Record<string, V>> {
  if (!(id in map)) return map;
  const next: Record<string, V> = {};
  for (const [key, value] of Object.entries(map)) {
    if (key !== id) next[key] = value;
  }
  return next;
}

export const useAutosaveStatusStore = create<AutosaveStatusState>()((set) => ({
  pending: {},
  saving: {},
  setPending: (id, entry) => {
    set((state) => ({ pending: { ...state.pending, [id]: entry } }));
  },
  clearPending: (id) => {
    set((state) => {
      const next = without(state.pending, id);
      return next === state.pending ? state : { pending: next };
    });
  },
  setSaving: (id, scopeId) => {
    set((state) => ({ saving: { ...state.saving, [id]: scopeId } }));
  },
  clearSaving: (id) => {
    set((state) => {
      const next = without(state.saving, id);
      return next === state.saving ? state : { saving: next };
    });
  },
}));

const inScope = (entryScope: string | null, scopeId?: string | null): boolean =>
  scopeId === undefined || entryScope === scopeId;

/**
 * The soonest pending autosave time within a scope, or `null` when nothing is pending. Pass a
 * `scopeId` to ask about one person's cards (pedigree); omit it to ask across every scope.
 */
export function selectSoonestSavesAt(
  state: AutosaveStatusState,
  scopeId?: string | null,
): number | null {
  let soonest: number | null = null;
  for (const entry of Object.values(state.pending)) {
    if (!inScope(entry.scopeId, scopeId)) continue;
    if (soonest === null || entry.savesAt < soonest) soonest = entry.savesAt;
  }
  return soonest;
}

/** Is any commit currently in flight within the scope? */
export function selectAnySaving(state: AutosaveStatusState, scopeId?: string | null): boolean {
  for (const entryScope of Object.values(state.saving)) {
    if (inScope(entryScope, scopeId)) return true;
  }
  return false;
}
